import React, { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  makeStyles,
} from "@material-ui/core";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { dispatchTicketUpdated } from "../../utils/ticketEvents";

const NO_TICKET_FUNNEL = "__no_ticket_funnel__";
const NO_TICKET_STAGE = "__no_ticket_stage__";

const getReadableTextColor = color => {
  const hex = (color || "#ffffff").replace("#", "");
  const normalized =
    hex.length === 3
      ? hex.split("").map(char => `${char}${char}`).join("")
      : hex.padEnd(6, "f").slice(0, 6);
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

  return luminance > 150 ? "#243040" : "#ffffff";
};

const normalizeFunnels = funnels => {
  if (!Array.isArray(funnels)) return [];

  return funnels.map(funnel => ({
    id: funnel.id,
    name: funnel.name || "Embudo",
    stages: Array.isArray(funnel.stages) ? funnel.stages : [],
    ticketStages: (funnel.tickets || []).reduce((acc, item) => {
      acc[String(item.ticketId)] = item.stageId;
      return acc;
    }, {}),
  }));
};

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 1fr) minmax(180px, 210px) minmax(180px, 210px)",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    background: theme.palette.background.paper,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    },
  },
  input: {
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      gridColumn: "1 / -1",
    },
  },
  funnelControl: {
    minWidth: 0,
  },
  stageControl: {
    minWidth: 0,
  },
  selectValue: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    minHeight: 24,
  },
  selectedChip: {
    height: 22,
    maxWidth: "100%",
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
}));

const TicketLabels = ({ ticket, onUpdate }) => {
  const classes = useStyles();
  const [labels, setLabels] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState("");
  const [selectedStageId, setSelectedStageId] = useState("");
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFunnels, setLoadingFunnels] = useState(false);
  const [savingFunnel, setSavingFunnel] = useState(false);

  const updateTicketFunnelStage = ({ funnelId, stageId }) => {
    if (!ticket?.id || !funnelId) return;

    setFunnels(prevFunnels =>
      prevFunnels.map(funnel => {
        const nextTicketStages = { ...funnel.ticketStages };

        if (String(funnel.id) === String(funnelId) && stageId) {
          nextTicketStages[String(ticket.id)] = stageId;
        } else {
          delete nextTicketStages[String(ticket.id)];
        }

        return { ...funnel, ticketStages: nextTicketStages };
      })
    );
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/labels", {
          params: { connectionId: ticket?.connectionId },
        });
        setLabels(data || []);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    })();
  }, [ticket?.connectionId]);

  useEffect(() => {
    if (!ticket?.id) return;

    (async () => {
      setLoadingFunnels(true);
      try {
        const { data } = await api.get("/funnels");
        setFunnels(normalizeFunnels(data.funnels || []));
      } catch (err) {
        toastError(err);
      }
      setLoadingFunnels(false);
    })();
  }, [ticket?.id]);

  useEffect(() => {
    if (!ticket?.id) return undefined;

    const socket = openSocket();

    socket.on("connect", () => {
      socket.emit("joinNotification");
    });

    socket.on("funnel", data => {
      if (
        data.action === "ticket:move" &&
        String(data.ticketId) === String(ticket.id)
      ) {
        updateTicketFunnelStage({
          funnelId: data.funnelId,
          stageId: data.stageId,
        });
      }

      if (
        ["create", "update", "delete", "stage:create", "stage:update", "stage:delete"].includes(
          data.action
        )
      ) {
        api
          .get("/funnels")
          .then(({ data: responseData }) =>
            setFunnels(normalizeFunnels(responseData.funnels || []))
          )
          .catch(toastError);
      }
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]);

  useEffect(() => {
    const ticketLabels = ticket?.labels || [];
    setSelectedLabelIds(ticketLabels.map(label => label.id));
  }, [ticket?.id, ticket?.labels]);

  const currentFunnelAssignment = useMemo(() => {
    const ticketId = String(ticket?.id || "");
    if (!ticketId) return null;

    return funnels.reduce((assignment, funnel) => {
      if (assignment) return assignment;
      const stageId = funnel.ticketStages[ticketId];
      return stageId ? { funnelId: funnel.id, stageId } : null;
    }, null);
  }, [funnels, ticket?.id]);

  useEffect(() => {
    if (!ticket?.id) {
      setSelectedFunnelId("");
      setSelectedStageId("");
      return;
    }

    setSelectedFunnelId(currentFunnelAssignment?.funnelId || "");
    setSelectedStageId(currentFunnelAssignment?.stageId || "");
  }, [currentFunnelAssignment, ticket?.id]);

  const selectedFunnel = useMemo(
    () => funnels.find(funnel => String(funnel.id) === String(selectedFunnelId)),
    [funnels, selectedFunnelId]
  );

  const handleLabelsChange = async event => {
    if (!ticket?.id) return;
    const labelIds = event.target.value;
    const previousLabelIds = selectedLabelIds;
    const previousTicket = ticket;
    const optimisticLabels = labelIds
      .map(labelId => labels.find(label => String(label.id) === String(labelId)))
      .filter(Boolean);
    const optimisticTicket = {
      ...ticket,
      labels: optimisticLabels,
      unreadMessages: 0,
    };

    setSelectedLabelIds(labelIds);
    dispatchTicketUpdated(optimisticTicket);
    if (onUpdate) onUpdate(optimisticTicket);

    try {
      const { data } = await api.put(`/tickets/${ticket.id}`, {
        labelIds,
      });
      dispatchTicketUpdated(data);
      if (onUpdate) onUpdate(data);
    } catch (err) {
      setSelectedLabelIds(previousLabelIds);
      dispatchTicketUpdated(previousTicket);
      if (onUpdate) onUpdate(previousTicket);
      toastError(err);
    }
  };

  const handleFunnelChange = event => {
    const funnelId = event.target.value;
    const nextFunnel = funnels.find(funnel => String(funnel.id) === String(funnelId));
    setSelectedFunnelId(funnelId);
    setSelectedStageId(nextFunnel?.ticketStages?.[String(ticket?.id)] || "");
  };

  const handleStageChange = async event => {
    if (!ticket?.id || !selectedFunnelId) return;

    const stageId = event.target.value;
    const previousAssignment = currentFunnelAssignment;
    setSelectedStageId(stageId);
    setSavingFunnel(true);

    try {
      if (
        previousAssignment?.funnelId &&
        String(previousAssignment.funnelId) !== String(selectedFunnelId)
      ) {
        await api.put(`/funnels/${previousAssignment.funnelId}/tickets/${ticket.id}/move`, {
          stageId: null,
        });
      }

      await api.put(`/funnels/${selectedFunnelId}/tickets/${ticket.id}/move`, {
        stageId: stageId || null,
      });
      updateTicketFunnelStage({
        funnelId: selectedFunnelId,
        stageId,
      });
    } catch (err) {
      toastError(err);
      setSelectedStageId(selectedFunnel?.ticketStages?.[String(ticket.id)] || "");
    }

    setSavingFunnel(false);
  };

  return (
    <Paper square elevation={0} className={classes.wrapper}>
      <FormControl
        className={classes.input}
        margin="dense"
        size="small"
        variant="outlined"
        disabled={loading}
      >
        <InputLabel>{i18n.t("ticketLabels.inputLabel")}</InputLabel>
        <Select
          multiple
          value={selectedLabelIds}
          onChange={handleLabelsChange}
          label={i18n.t("ticketLabels.inputLabel")}
          renderValue={selected => (
            <div className={classes.selectValue}>
              {selected.length ? (
                labels
                  .filter(label =>
                    selected.some(labelId => String(labelId) === String(label.id))
                  )
                  .map(label => (
                    <Chip
                      key={label.id}
                      label={label.name}
                      className={classes.selectedChip}
                      style={{
                        backgroundColor: label.color,
                        color: getReadableTextColor(label.color),
                      }}
                    />
                  ))
              ) : (
                <span>&ZeroWidthSpace;</span>
              )}
            </div>
          )}
        >
          {labels.map(label => (
            <MenuItem key={label.id} value={label.id}>
              <Checkbox
                style={{ color: label.color }}
                checked={selectedLabelIds.some(labelId => String(labelId) === String(label.id))}
              />
              <ListItemText primary={label.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        className={classes.funnelControl}
        margin="dense"
        size="small"
        variant="outlined"
        disabled={loadingFunnels || savingFunnel}
      >
        <InputLabel>Embudo</InputLabel>
        <Select
          value={selectedFunnelId || NO_TICKET_FUNNEL}
          onChange={event =>
            handleFunnelChange({
              ...event,
              target: {
                ...event.target,
                value:
                  event.target.value === NO_TICKET_FUNNEL ? "" : event.target.value,
              },
            })
          }
          label="Embudo"
        >
          <MenuItem value={NO_TICKET_FUNNEL}>Sin embudo</MenuItem>
          {funnels.map(funnel => (
            <MenuItem key={funnel.id} value={funnel.id}>
              {funnel.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        className={classes.stageControl}
        margin="dense"
        size="small"
        variant="outlined"
        disabled={!selectedFunnelId || loadingFunnels || savingFunnel}
      >
        <InputLabel>Etapa</InputLabel>
        <Select
          value={selectedStageId || NO_TICKET_STAGE}
          onChange={event =>
            handleStageChange({
              ...event,
              target: {
                ...event.target,
                value: event.target.value === NO_TICKET_STAGE ? "" : event.target.value,
              },
            })
          }
          label="Etapa"
        >
          <MenuItem value={NO_TICKET_STAGE}>Sin etapa</MenuItem>
          {(selectedFunnel?.stages || []).map(stage => (
            <MenuItem key={stage.id} value={stage.id}>
              {stage.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Paper>
  );
};

export default TicketLabels;

